import { WAS_BACK_END_API_URL, URL_VERSION } from '../../../../constants';
import { fetchAuthenticated } from '../../../../controllers';
import { useCallback, useContext, useEffect, useState } from 'react';
import './PatientBloodSamples.scss';
import { Header } from '../../../../components/common/table/Header/Header';
import { HeaderCell } from '../../../../components/common/table/HeaderCell/HeaderCell';
import { FilterButton } from '../../../../components/common/buttons/iconButtons/FilterButton';
import { SampleHistoryRow } from './SampleHistoryRow/SampleHistoryRow';
import { UserProps } from '../../../../shared/types/types';
import { decryptObject } from '../../../../components/Encryption/obfuscationHandler';
import { v4 } from 'uuid';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';

interface PatientBloodSamplesProps {
  patientId?: string;
  token: string | null;
}

export interface BloodSample {
  _id: string;
  body: BodyProps;
  header: HeaderProps;
}
interface HeaderProps {
  epoch_nsec: string;
  epoch_nsec_as_local: string;
  epoch_nsec_as_utc: string;
  patient_id: string;
  user: UserProps;
}
interface BodyProps {
  draw_date_time_utc: string;
  encrypt_vial_id: string;
  icp_oes_boron_concentration_ppm: string | null;
}

export interface BloodSamplesServiceResult {
  status: string;
  total: number;
  items: Array<BloodSample>;
  errors: string[];
}

export interface BloodSamplesAjaxResult {
  result: BloodSamplesServiceResult;
}

export const PatientBloodSamples = ({ patientId, token }: PatientBloodSamplesProps) => {
  const [bloodSamples, setBloodSamples] = useState<BloodSample[] | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiData, setApiData] = useState<BloodSamplesAjaxResult>();
  const [serverError, setServerError] = useState();
  const { addBannerMessage } = useContext(BannerMessageContext);

  const getBloodSampleData = useCallback(async () => {
    const config: RequestInit = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    if (!patientId) return;
    setIsLoading(true);
    const url = WAS_BACK_END_API_URL + `${URL_VERSION}/patients/${patientId}/blood-samples`;

    const response = await fetchAuthenticated<BloodSamplesAjaxResult>(url, config);

    try {
      if (response?.ok) {
        setIsLoading(false);
        setApiData(response?.parsedBody);
      }
    } catch (error) {
      setIsLoading(false);
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Uh oh! There was an error getting the Blood Sample Data. Please refresh to try again.',
      });
    }
  }, [token, addBannerMessage, patientId]);

  useEffect(() => {
    try {
      getBloodSampleData();
    } catch (error: any) {
      setServerError(error);
    }
  }, [getBloodSampleData]);

  useEffect(() => {
    async function decryptData() {
      if (apiData?.result?.items) {
        try {
          await decryptObject(apiData.result, token, null);
          setBloodSamples(apiData?.result.items);
        } catch {
          setBloodSamples(null);
        }
      }
    }
    decryptData();
  }, [apiData, token]);

  const updateApiData = (patchedBloodSample: BloodSample) => {
    const newBloodSamples = [];
    if (!bloodSamples) return;
    const patchedBloodSampleId = patchedBloodSample._id;
    for (let i = 0; i < bloodSamples?.length; i++) {
      if (bloodSamples[i]._id !== patchedBloodSampleId) {
        newBloodSamples.push(bloodSamples[i]);
      } else {
        newBloodSamples.push(patchedBloodSample);
      }
    }
    setBloodSamples(newBloodSamples);
  };
  return (
    <div className="patient-blood-sample-container" data-testid="patient-blood-samples-card">
      <>
        {isLoading && <p>Loading...</p>}
        {serverError && <p>Error:</p>}
        {bloodSamples && bloodSamples.length > 0 && (
          <table className="table-container">
            <Header>
              <HeaderCell>
                <div>vial id</div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell>
                <div className="time-format-cell">
                  <div>boron concentration</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>¹⁰B μg/g</div>
                </div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell>
                <div className="time-format-cell">
                  <div>draw date</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>dd:MMM:YYYY</div>
                </div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell>
                <div className="time-format-cell">
                  <div>draw time</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>HH:mm:ss</div>
                </div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
              <HeaderCell>
                <div>last updated</div>
                <FilterButton onClick={() => {}} width={24} height={24} />
              </HeaderCell>
            </Header>
            <tbody className="body-container">
              {bloodSamples.map((sample, index) => (
                <SampleHistoryRow
                  token={token}
                  sample={sample}
                  index={index}
                  patientId={patientId}
                  key={sample._id}
                  updateApiData={(data: BloodSample) => updateApiData(data)}
                />
              ))}
            </tbody>
          </table>
        )}
      </>
    </div>
  );
};
